import {
  CircularProgress,
  ImageList,
  ImageListItem,
  Stack,
  Typography,
  StackProps,
} from "@mui/material";
import {
  useRef,
  useMemo,
  useState,
  Dispatch,
  useEffect,
  useCallback,
  SetStateAction,
  FunctionComponent,
  PropsWithChildren,
} from "react";
import {
  PinTypeResponse,
  BuildingResponse,
  WebAssetResponse,
  AttachmentResponse,
} from "@akitabox/api-client";
import {
  GridFilterModel,
  GridRowSelectionModel,
  GridRowParams,
  GridRowId,
  getGridStringOperators,
  getGridSingleSelectOperators,
} from "@mui/x-data-grid-pro";

import { api } from "../api";
import { makeUseServiceCall } from "../hooks/useServiceCall";
import {
  AssetAttachmentSelection,
  AssetSelectionTemplate,
} from "./AssetSelectionDialog";
import { AssetSelectionDataTemplate } from "./AssetSelectionDataTemplate";
import { AbxDataGrid, AbxGridColDef } from "../lists/abx-data-grid/AbxDataGrid";
import HideImageIcon from "@mui/icons-material/HideImage";

/**
 * Simpler version of the built-in filter operators for `singleSelect` columns
 * - Shows `is` operator only
 * @see https://mui.com/x/react-data-grid/filtering/customization/#remove-an-operator
 */
const singleSelectIsOperator = getGridSingleSelectOperators().filter(
  (operator) => operator.value === "is"
);

/**
 * Simpler version of the built-in filter operators for `string` columns
 * - Shows `contains` operator only
 * @see https://mui.com/x/react-data-grid/filtering/customization/#remove-an-operator
 */
const stringContainsOperator = getGridStringOperators().filter(
  (operator) => operator.value === "contains"
);

/**
 * Constant for the limit of pin types to be fetched
 */
const PIN_TYPES_FETCH_LIMIT = 500;

export interface AssetSelectionDataGridFilters {
  name?: string;
  pinType?: string;
  building?: string;
}

export interface AssetSelectionDataGridProps {
  organizationId: string;
  selectedTemplate: AssetSelectionTemplate;
  selectedAttachments: AssetAttachmentSelection;
  setSelectedAssets: Dispatch<SetStateAction<WebAssetResponse[]>>;
  setSelectedTemplate: (template: AssetSelectionTemplate) => void;
  setSelectedAttachments: Dispatch<SetStateAction<AssetAttachmentSelection>>;
  canSelectImages: boolean;
}

export const AssetSelectionDataGrid: FunctionComponent<
  AssetSelectionDataGridProps
> = ({
  organizationId,
  selectedTemplate,
  selectedAttachments,
  setSelectedAssets,
  setSelectedTemplate,
  setSelectedAttachments,
  canSelectImages,
}) => {
  const orgPinTypesFetchStep = useRef(0);
  const assetsMemory = useRef<WebAssetResponse[][]>([]);

  const [filters, setFilters] = useState<AssetSelectionDataGridFilters>();
  const [buildingPinTypes, setBuildingPinTypes] = useState<PinTypeResponse[]>();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [selectedThumbnails, setSelectedThumbnails] = useState<
    Map<string, string>
  >(new Map());

  const { data: assetsResponse, isLoading } = makeUseServiceCall(
    api.web.getAssets
  )({
    sort: "name,asc",
    insensitive: true,
    orgId: organizationId,
    decommissioned: false,
    // filters
    name: filters?.name,
    pinType: filters?.pinType,
    building: filters?.building,
    // pagination
    limit: paginationModel.pageSize,
    skip: paginationModel.page * paginationModel.pageSize,
  });

  const { isLoading: isCounting, data: assetsCountResponse } =
    makeUseServiceCall(api.assets.count)({
      organizationId,
      decommissioned: false,
      // filters
      name: filters?.name,
      pinType: filters?.pinType,
      building: filters?.building,
    });

  const { data: buildingsResponse } = makeUseServiceCall(
    api.buildings.getByOrganization
  )({
    skip: 0,
    limit: 1000,
    organizationId,
  });

  const { data: orgPinTypesResponse, mutate: mutateOrgPinTypesResponse } =
    makeUseServiceCall(api.pinTypes.getByOrganization)({
      skip: 0,
      organizationId,
      limit: PIN_TYPES_FETCH_LIMIT,
    });

  const { data: orgPinTypesCountResponse } = makeUseServiceCall(
    api.pinTypes.count
  )({ organizationId });

  const orgPinTypesCount = useMemo(() => {
    if (!orgPinTypesCountResponse) return 0;
    return orgPinTypesCountResponse.data.count;
  }, [orgPinTypesCountResponse]);

  const assetsCount = useMemo(() => {
    if (!assetsCountResponse) return 0;
    return assetsCountResponse.data.count;
  }, [assetsCountResponse]);

  const assets = useMemo(
    () => (assetsResponse ? assetsResponse.data : []),
    [assetsResponse]
  );

  const buildings = useMemo(
    () => (buildingsResponse ? buildingsResponse.data : []),
    [buildingsResponse]
  );

  const orgPinTypes = useMemo(() => {
    if (!orgPinTypesResponse) return [];

    return orgPinTypesResponse.data.reduce<PinTypeResponse[]>(
      (previous, current) => {
        const hasPinType = previous.some(
          (pinType) => pinType.name === current.name
        );

        if (!hasPinType) previous.push(current);

        return previous;
      },
      []
    );
  }, [orgPinTypesResponse]);

  const columns = useMemo<AbxGridColDef<WebAssetResponse>[]>(
    () => [
      {
        field: "name",
        type: "string",
        headerName: "Name",
        abxGridColType: "basic",
        filterOperators: stringContainsOperator,
      },
      {
        field: "building",
        type: "singleSelect",
        headerName: "Building",
        valueOptions: buildings,
        abxGridColType: "basic",
        filterOperators: singleSelectIsOperator,
        renderCell: ({ row: asset }) => asset.building.name,
        getOptionValue: (value?: BuildingResponse) => value?._id,
        getOptionLabel: (value?: BuildingResponse) => value?.name,
      },
      {
        field: "pinType",
        type: "singleSelect",
        headerName: "Category",
        abxGridColType: "basic",
        filterOperators: singleSelectIsOperator,
        valueOptions: buildingPinTypes || orgPinTypes,
        renderCell: ({ row: asset }) => asset.pinType.name,
        getOptionValue: (value?: PinTypeResponse) => value?.name,
        getOptionLabel: (value?: PinTypeResponse) => value?.name,
      },
    ],
    [buildings, buildingPinTypes, orgPinTypes]
  );

  const getPinTypesIds = useCallback(
    (pinTypeName: string, pinTypes: PinTypeResponse[]) =>
      pinTypes
        .filter((pinType) => pinType.name === pinTypeName)
        .map((pinType) => pinType._id)
        .join(),
    []
  );

  const onFilterChange = useCallback(
    async ({ items }: GridFilterModel) => {
      const filters: AssetSelectionDataGridFilters = {};

      const nameFilter = items.find(({ field }) => field === "name");
      if (nameFilter && nameFilter.value) {
        filters.name = nameFilter.value;
      }

      let buildingPinTypes: PinTypeResponse[] | undefined;
      const buildingFilter = items.find(({ field }) => field === "building");
      if (buildingFilter && buildingFilter.value) {
        filters.building = buildingFilter.value;

        try {
          const { data } = await api.pinTypes.getByOrganization({
            organizationId,
            building: filters.building,
          });
          buildingPinTypes = data;
        } catch (e) {
          throw new Error(e instanceof Error ? e.message : String(e));
        }
      }

      setBuildingPinTypes(buildingPinTypes);

      const pinTypeFilter = items.find(({ field }) => field === "pinType");
      if (pinTypeFilter && pinTypeFilter.value) {
        let ids = "";

        if (buildingPinTypes) {
          ids = getPinTypesIds(pinTypeFilter.value, buildingPinTypes);
        } else if (orgPinTypesResponse) {
          ids = getPinTypesIds(pinTypeFilter.value, orgPinTypesResponse.data);
        }

        filters.pinType = `$in,${ids}`;
      }

      setFilters(filters);
    },
    [organizationId, getPinTypesIds, orgPinTypesResponse]
  );

  const fetchAttachments = useCallback(
    async (asset: WebAssetResponse) => {
      if (selectedAttachments[asset._id]) {
        return;
      }

      // initialize entry that will hold the images for this asset
      // marks it into a "Loading" state
      const attachmentLoading = {
        [asset._id]: { attachments: [], loading: true },
      };
      setSelectedAttachments((prevValue) => {
        return {
          ...prevValue,
          ...attachmentLoading,
        };
      });

      let attachments: AttachmentResponse[] = [];
      const {
        data: { count = 0 },
      } = await api.attachments.count({
        building: asset.building._id,
        linksEntityId: asset._id,
        linksEntityType: "asset",
      });

      let skip = 0;
      const limit = 1000;

      while (count > attachments.length) {
        const { data } = await api.attachments.getByBuilding({
          building: asset.building._id,
          linksEntityId: asset._id,
          linksEntityType: "asset",
          limit,
          skip,
        });
        attachments = attachments.concat(data);
        skip += limit;
      }

      const attachmentLoaded = { [asset._id]: { attachments, loading: false } };
      setSelectedAttachments((prevValue) => {
        return {
          ...prevValue,
          ...attachmentLoaded,
        };
      });
    },
    [selectedAttachments, setSelectedAttachments]
  );

  const handleAssetThumbnailSelection = useCallback(
    (asset: WebAssetResponse, attachment: AttachmentResponse) => {
      const has = selectedThumbnails.has(asset._id);
      const copyThumbnails = new Map(selectedThumbnails);
      if (!has) {
        // add
        copyThumbnails.set(
          asset._id,
          attachment.document.public_thumbnail_url_mini
        );
      } else if (
        has &&
        selectedThumbnails.get(asset._id) ===
          attachment.document.public_thumbnail_url_mini
      ) {
        // remove
        copyThumbnails.delete(asset._id);
      } else if (
        has &&
        selectedThumbnails.get(asset._id) !==
          attachment.document.public_thumbnail_url_mini
      ) {
        copyThumbnails.set(
          asset._id,
          attachment.document.public_thumbnail_url_mini
        );
      }
      setSelectedThumbnails(copyThumbnails);

      if (selectedAttachments[asset._id]) {
        const updatedAttachment = {
          [asset._id]: {
            default: attachment,
            attachments: selectedAttachments[asset._id].attachments,
            loading: selectedAttachments[asset._id].loading,
          },
        };

        setSelectedAttachments((prevValue) => {
          return {
            ...prevValue,
            ...updatedAttachment,
          };
        });
      }
    },
    [selectedThumbnails, selectedAttachments, setSelectedAttachments]
  );

  const getDetailPanelContent = useCallback(
    (params: GridRowParams<WebAssetResponse>) => {
      const asset = params.row;

      if (!selectedAttachments[asset._id]) {
        return (
          <DetailPanelContainer>
            <Typography component="span" variant="body2">
              Select this asset (mark checkbox) to preview its images
            </Typography>
          </DetailPanelContainer>
        );
      }
      const selectedAttacment = selectedAttachments[asset._id];

      if (selectedAttacment.loading) {
        return (
          <DetailPanelContainer direction="row">
            <CircularProgress />
            <Typography component="span" variant="body2">
              Loading Images...
            </Typography>
          </DetailPanelContainer>
        );
      } else if (
        !selectedAttacment.loading &&
        selectedAttacment.attachments.length === 0
      ) {
        return (
          <DetailPanelContainer direction="row">
            <HideImageIcon />
            <Typography component="span" variant="body2">
              No Images
            </Typography>
          </DetailPanelContainer>
        );
      }

      if (canSelectImages) {
        return (
          <DetailPanelContainer>
            <Typography component="span" variant="body2">
              Optionally select an image below to be used as the asset&apos;s
              thumbnail
            </Typography>
            <ImageList cols={6} rowHeight={60}>
              {selectedAttacment.attachments.map((attachment) => {
                const selected =
                  selectedThumbnails.has(asset._id) &&
                  selectedThumbnails.get(asset._id) ===
                    attachment.document.public_thumbnail_url_mini;
                return (
                  <ImageListItem
                    key={attachment._id}
                    css={() => ({ justifyContent: "center" })}
                  >
                    <img
                      css={() => ({
                        border: selected
                          ? "2px solid #666"
                          : "2px solid transparent",
                        boxShadow: selected
                          ? "rgba(0, 0, 0, 0.35) 0px 2.75px 5px;"
                          : "none",
                        aspectRatio: "4 / 3",
                        objectFit: "contain",
                      })}
                      onClick={() =>
                        handleAssetThumbnailSelection(asset, attachment)
                      }
                      src={attachment.document.public_thumbnail_url_mini}
                      srcSet={attachment.document.public_thumbnail_url_mini}
                      alt={attachment.document.name}
                      loading="lazy"
                    ></img>
                  </ImageListItem>
                );
              })}
            </ImageList>
          </DetailPanelContainer>
        );
      } else {
        return (
          <DetailPanelContainer>
            <ImageList cols={6} rowHeight={60}>
              {selectedAttacment.attachments.map((attachment) => {
                return (
                  <ImageListItem
                    key={attachment._id}
                    css={() => ({ justifyContent: "center" })}
                  >
                    <img
                      css={() => ({
                        aspectRatio: "4 / 3",
                        objectFit: "contain",
                      })}
                      src={attachment.document.public_thumbnail_url_mini}
                      srcSet={attachment.document.public_thumbnail_url_mini}
                      alt={attachment.document.name}
                      loading="lazy"
                    ></img>
                  </ImageListItem>
                );
              })}
            </ImageList>
          </DetailPanelContainer>
        );
      }
    },
    [
      handleAssetThumbnailSelection,
      selectedAttachments,
      selectedThumbnails,
      canSelectImages,
    ]
  );

  const findAssetInMemory = useCallback(
    (assetId: GridRowId) => {
      const flatted = assetsMemory.current.flat();
      return flatted.find((asset) => asset._id === assetId);
    },
    [assetsMemory]
  );

  const onRowSelectionModelChange = useCallback(
    (assetsIds: GridRowSelectionModel) => {
      const selectedAssets = assetsIds.reduce<WebAssetResponse[]>(
        (accumulator, current) => {
          const asset = findAssetInMemory(current);
          if (asset) {
            fetchAttachments(asset);
            accumulator.push(asset);
          }

          return accumulator;
        },
        []
      );

      setSelectedAssets(selectedAssets);
    },
    [findAssetInMemory, fetchAttachments, setSelectedAssets]
  );

  useEffect(() => {
    if (!assetsResponse) return;
    const { page } = paginationModel;
    // matrix that stores the list of assets
    // indexed by the current pagination page
    assetsMemory.current[page] = assetsResponse.data;
  }, [assetsResponse, paginationModel]);

  useEffect(() => {
    if (
      !orgPinTypesResponse ||
      orgPinTypesCount === 0 ||
      orgPinTypesResponse.data.length === orgPinTypesCount
    ) {
      return;
    }

    (async () => {
      try {
        orgPinTypesFetchStep.current += PIN_TYPES_FETCH_LIMIT;
        const { data } = await api.pinTypes.getByOrganization({
          organizationId,
          limit: PIN_TYPES_FETCH_LIMIT,
          skip: orgPinTypesFetchStep.current,
        });

        mutateOrgPinTypesResponse(
          {
            ...orgPinTypesResponse,
            data: [...orgPinTypesResponse.data, ...data],
          },
          { revalidate: false }
        );
      } catch (e) {
        throw new Error(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [
    organizationId,
    orgPinTypesCount,
    orgPinTypesResponse,
    mutateOrgPinTypesResponse,
  ]);

  return (
    <Stack flexWrap="nowrap">
      <AssetSelectionDataTemplate
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
      />
      <AbxDataGrid
        autoHeight
        pagination
        rows={assets}
        columns={columns}
        checkboxSelection
        disableColumnFilter
        filterMode="server"
        disableColumnPinning
        rowCount={assetsCount}
        paginationMode="server"
        keepNonExistentRowsSelected
        loading={isLoading || isCounting}
        paginationModel={paginationModel}
        slots={{ headerFilterMenu: null }}
        getDetailPanelHeight={() => "auto"}
        onFilterModelChange={onFilterChange}
        pageSizeOptions={[paginationModel.pageSize]}
        onPaginationModelChange={setPaginationModel}
        getDetailPanelContent={getDetailPanelContent}
        onRowSelectionModelChange={onRowSelectionModelChange}
        /**
         * New/unstable MUI X Pro feature
         * @see https://mui.com/x/react-data-grid/filtering/header-filters/
         */
        unstable_headerFilters
      />
    </Stack>
  );
};

/**
 * Simple container that wraps the single asset image selection section
 *
 * @param props - React `children` and MUI Stack `direction` (optional)
 * @returns The `<DetailPanelContainer />` function component
 */
const DetailPanelContainer: FunctionComponent<
  Required<PropsWithChildren> & Pick<StackProps, "direction">
> = ({ children, direction }) => (
  <Stack
    alignItems="center"
    direction={direction}
    justifyContent="center"
    gap={(theme) => theme.spacing(1)}
    padding={(theme) => theme.spacing(2)}
  >
    {children}
  </Stack>
);
